import React, { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import BarChart from "../shared/charts/BarChart";
import {
  SlidersHorizontal,
  Users,
  TrendingUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Info,
  Percent,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import PlatformCalendar from "../shared_components/platformCalendar";
import "../../css/platformCalendar.css";
import "../../css/CandidateReports.css";
import { calendarOverrideStyles } from "../shared_components/datePickerStyles";
import host from "../../global";
import DropDownWithSearch from "../shared/DropDownWIthSearch";
import { useTheme } from "../../hooks/useTheme";

// Custom Tooltip for Charts — styled to match the org-admin Dashboard's bar chart tooltip
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        borderRadius: "4px",
        border: "1px solid #E5E7EB",
        padding: "8px 12px",
      }}
    >
      <p style={{ fontSize: "13px", fontWeight: 700, marginBottom: 4, color: "#707070" }}>
        {data.name}
      </p>
      <p style={{ fontSize: "12px", color: "#374151", padding: "2px 0" }}>
        Score: {data.score}/100
      </p>
      {data.candidateJobRole && data.candidateJobRole !== "Unknown" && (
        <p style={{ fontSize: "11px", color: "#6B7280" }}>Position: {data.candidateJobRole}</p>
      )}
    </div>
  );
};

const KPITile = ({ icon, label, value, description }) => (
  <div
    className="w-full dark:bg-gradient-to-r dark:from-[#232142]/10 dark:to-[#7E64A3]/10 bg-bgSecondary rounded-xl flex flex-col"
    title={description}
  >
    <div className="m-4 gap-2 items-center space-y-2">
      <div className="flex items-center space-x-4 font-semibold dark:text-[#AEB9E1] text-[#404040]">
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="font-bold text-4xl dark:text-[#FFFFFF] text-[#707070]">
        <span>{value}</span>
      </div>
    </div>
  </div>
);

const parseScore = (candidate) =>
  candidate.average_score === "NA" ? 0 : parseFloat(candidate.average_score);

const PAGE_SIZE = 50;
const emptySummary = { total_candidates: 0, average_score: 0, selected_count: 0 };

const CandidateAnalyticsDashboard = () => {
  const [candidates, setCandidates] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState(emptySummary);
  // Selected Rate is always computed across every job (ignoring the Job Role
  // filter), so it needs its own all-jobs summary. When "All" is already
  // selected, `summary` above already covers every job — reuse it instead of
  // firing a duplicate request.
  const [allJobsSummary, setAllJobsSummary] = useState(emptySummary);
  const [isLoadingJobPositions, setIsLoadingJobPositions] = useState(true);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);
  const [isLoadingAllJobsCandidates, setIsLoadingAllJobsCandidates] = useState(false);
  const isLoading = isLoadingJobPositions || isLoadingCandidates || isLoadingAllJobsCandidates;
  // Min Score now drives a server-side filter, so typing must be debounced —
  // `minScoreInput` updates the field instantly, `minScore` (debounced) is
  // what's actually sent to the API.
  const [minScoreInput, setMinScoreInput] = useState(0);
  const [minScore, setMinScore] = useState(0);
  const [dateRange, setDateRange] = useState([null, null]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedJobPosition, setSelectedJobPosition] = useState("All");
  const [jobPositions, setJobPositions] = useState([]);
  const accessToken = Cookies.get("accessToken");
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedJobPosition: locationJobPosition } = location.state || {};

  const { theme } = useTheme();

  const calendarRef = useRef(null);

  // Format Date Functions
  const formatDate = (date, isEndDate = false) => {
    if (!date) return null;
    const formattedDate = new Date(date);
    if (isEndDate) {
      formattedDate.setHours(23, 59, 59, 999);
    } else {
      formattedDate.setHours(0, 0, 0, 0);
    }
    return formattedDate.toISOString();
  };

  const formatDateRange = (dateRange) => {
    if (!dateRange[0] || !dateRange[1]) return "All Time";
    return `${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`;
  };

  // Handle Errors
  const handleError = (error) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 401) {
        Cookies.remove("accessToken");
        toast.error(`Unauthorized: ${data.detail}`);
        navigate("/");
      } else if (status === 404) {
        toast.warning(`Not Found: ${data.detail}`);
      } else {
        toast.error(`Error: ${data.detail}`);
      }
    } else {
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  // Debounce Min Score before it drives the (now server-side) filter, so
  // typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setMinScore(minScoreInput), 400);
    return () => clearTimeout(timer);
  }, [minScoreInput]);

  // Fetch Job Positions
  useEffect(() => {
    const fetchJobPositions = async () => {
      try {
        setIsLoadingJobPositions(true);
        const response = await axios.get(`${host}/jobroles`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setJobPositions(response.data || []);
        if (locationJobPosition?.job_role) {
          setSelectedJobPosition(locationJobPosition.job_role);
        }
        setIsLoadingJobPositions(false);
      } catch (error) {
        handleError(error);
        setIsLoadingJobPositions(false);
      }
    };
    fetchJobPositions();
  }, [accessToken, locationJobPosition]);

  // Fetch Candidates
  const [fromDate, toDate] = dateRange;
  // A single date picked (start only, no end yet) is a selection in progress —
  // wait for the range to be complete (or fully cleared) before calling the API.
  const isPartialDateSelection = Boolean(fromDate) !== Boolean(toDate);

  useEffect(() => {
    if (isPartialDateSelection) return;

    const fetchData = async () => {
      try {
        setIsLoadingCandidates(true);
        const params = {
          job_role: selectedJobPosition === "All" ? null : selectedJobPosition,
          start_date: formatDate(fromDate),
          end_date: formatDate(toDate, true),
          min_score: minScore > 0 ? minScore : null,
          page,
          page_size: PAGE_SIZE,
        };
        const response = await axios.get(`${host}/candidateanalytics`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        });
        setCandidates(response.data.candidates || []);
        setTotalCount(response.data.total_count || 0);
        setTotalPages(response.data.total_pages || 1);
        setSummary(response.data.summary || emptySummary);
        setIsLoadingCandidates(false);
      } catch (error) {
        handleError(error);
        setIsLoadingCandidates(false);
      }
    };
    fetchData();
  }, [accessToken, selectedJobPosition, fromDate, toDate, minScore, page, isPartialDateSelection]);

  // Fetch All-Jobs Summary (for Selected Rate, which always spans every job).
  // This only depends on the date range + min score, not on which specific
  // job is selected — so it's keyed on `isAllJobs` (not `selectedJobPosition`)
  // to avoid re-fetching every time you switch between two different job
  // roles. Requests `include_candidates: false` since only the aggregate
  // counts are needed here, not the full candidate list.
  const isAllJobs = selectedJobPosition === "All";

  useEffect(() => {
    if (isPartialDateSelection) return;

    if (isAllJobs) {
      // `summary` above already covers every job in this case.
      setAllJobsSummary(summary);
      setIsLoadingAllJobsCandidates(false);
      return;
    }

    const fetchAllJobsSummary = async () => {
      try {
        setIsLoadingAllJobsCandidates(true);
        const params = {
          job_role: null,
          start_date: formatDate(fromDate),
          end_date: formatDate(toDate, true),
          min_score: minScore > 0 ? minScore : null,
          include_candidates: false,
        };
        const response = await axios.get(`${host}/candidateanalytics`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        });
        setAllJobsSummary(response.data.summary || emptySummary);
        setIsLoadingAllJobsCandidates(false);
      } catch (error) {
        handleError(error);
        setIsLoadingAllJobsCandidates(false);
      }
    };
    fetchAllJobsSummary();
  }, [accessToken, isAllJobs, fromDate, toDate, minScore, isPartialDateSelection, summary]);

  // Handle Click Outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Current page's candidates, shaped for the chart. Min Score filtering and
  // sorting now happen server-side (needed for pagination to be consistent).
  const filteredCandidatesForChart = useMemo(() => {
    return candidates.map((candidate, index) => ({
      name: candidate.name,
      jobPosition: selectedJobPosition,
      score: parseScore(candidate),
      interviewId: candidate.interview_id,
      candidateJobRole: candidate.job_role || "Unknown",
      decision: candidate.decision || "PENDING",
      index,
    }));
  }, [candidates, selectedJobPosition]);

  // Average score across the full filtered set (not just the current page).
  const avgScore = summary.average_score || 0;

  // Selected ÷ Total candidates, across every job (ignores the Job Role filter,
  // still respects Date range + Min Score).
  const selectedRate = useMemo(() => {
    const { total_candidates, selected_count } = isAllJobs ? summary : allJobsSummary;
    if (!total_candidates) return 0;
    return (selected_count / total_candidates) * 100;
  }, [isAllJobs, summary, allJobsSummary]);

  // Handle Candidate Click
  const handleCandidateClick = (data) => {
    if (data?.interviewId) {
      navigate(`/org-admin/evaluationpage/${data.interviewId}`, {
        state: {
          candidateanalytics: true,
          selectedJobPosition: { job_role: data.jobPosition },
        },
      });
    }
  };

  const jobPositionOptions = useMemo(
    () => [
      { label: "All Jobs", value: "All" },
      ...jobPositions.map((role) => ({ label: role, value: role })),
    ],
    [jobPositions]
  );

  // DropDownWithSearch keeps its input focused after a click-selection (by
  // design, so the click isn't lost to a blur race) — drop focus afterwards
  // so the input doesn't sit there looking active once a value is picked.
  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleJobPositionSelect = (item) => {
    setSelectedJobPosition(item.value);
    setPage(1);
    blurActiveElement();
  };

  // Render Chart
  const renderChart = () => (
    <BarChart
      data={filteredCandidatesForChart}
      xKey="name"
      yKey="score"
      isDark={theme === "dark"}
      yDomain={[0, 100]}
      yLabel="Score"
      height="100%"
      colorForEntry={(entry) => (entry.score >= avgScore ? "#4D70EE" : "#A471F3")}
      onBarClick={handleCandidateClick}
      renderTooltip={(entry) => <CustomTooltip active payload={[{ payload: entry }]} />}
    />
  );

  return (
    <div className="min-h-screen flex flex-col w-full p-4 gap-2">
      <style>{calendarOverrideStyles(theme)}</style>
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 justify-end">
        <div className="w-64">
          <DropDownWithSearch
            selectedValue={jobPositionOptions.find((o) => o.value === selectedJobPosition) || null}
            dropdownData={jobPositionOptions}
            onSelect={handleJobPositionSelect}
            placeholder="Select Job Role"
            showClearButton={false}
          />
        </div>
        <div>
          <div className="relative" ref={calendarRef}>
            <button
              type="button"
              className="flex items-center justify-between w-52 px-4 py-2 border border-gray-300 rounded-[30px] bg-bgPrimary text-textPrimary focus:border-gray-400 focus:outline-none dark:glass-effect dark:bg-[linear-gradient(233.57deg,rgba(34,52,97,0.25)_-5.01%,rgba(11,10,31,0.05)_100%)] dark:border-[rgba(229,229,229,0.5)] dark:text-[rgba(216,216,216,1)] dark:focus:border-white"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
              aria-expanded={isCalendarOpen}
            >
              <span className="truncate">{formatDateRange(dateRange)}</span>
              {dateRange[0] || dateRange[1] ? (
                <X
                  className="w-4 h-4 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDateRange([null, null]);
                    setIsCalendarOpen(false);
                  }}
                />
              ) : (
                <ChevronDown className="w-4 h-4 flex-shrink-0" />
              )}
            </button>
            {isCalendarOpen && (
              <div
                className="candidate-analytics-calendar absolute right-0 mt-2 z-10"
                style={{
                  background: `${theme === "dark" ? "#0a1733" : ""}`,
                  backdropFilter: "blur(500px)",
                  backgroundColor: `${theme === "light" && "rgb(255 255 255)"}`,
                }}
              >
                <div className={`platform-calendar ${theme}`}>
                  <PlatformCalendar
                    onChange={(value) => {
                      setDateRange(value);
                      setPage(1);
                      if (value[0] && value[1]) {
                        setIsCalendarOpen(false);
                      }
                    }}
                    value={dateRange}
                    selectRange={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div>
          <input
            type="number"
            value={minScoreInput}
            onChange={(e) => {
              setMinScoreInput(Number(e.target.value));
              setPage(1);
            }}
            className="w-20 px-4 py-2 border border-gray-300 rounded-[30px] bg-bgPrimary text-textPrimary focus:border-gray-400 focus:outline-none dark:glass-effect dark:bg-[linear-gradient(233.57deg,rgba(34,52,97,0.25)_-5.01%,rgba(11,10,31,0.05)_100%)] dark:border-[rgba(229,229,229,0.5)] dark:text-[rgba(216,216,216,1)] dark:focus:border-white"
            min={0}
            max={100}
          />
        </div>
        {/* <button
          className="dark:text-[#FCD6F2E0] text-textSecondary hover:text-blue-400 px-2 text-sm !py-[5px]"
          onClick={() => {
            setSelectedJobPosition("All");
            setDateRange([null, null]);
            setMinScoreInput(0);
            setPage(1);
          }}
        >
          Reset Filters
        </button> */}
      </div>

      {isLoading && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 rounded-xl animate-pulse bg-gray-200 dark:bg-gray-900" />
            ))}
          </div>
          <div className="h-[620px] rounded-xl animate-pulse bg-gray-200 dark:bg-gray-900" />
        </div>
      )}

      {!isLoading && totalCount === 0 && (
        <div className="relative w-full p-10 dark:bg-gradient-to-r from-[#232142]/10 to-[#7E64A3]/10 bg-bgSecondary rounded-xl flex flex-col items-center justify-center">
          <Users className="w-12 h-12 dark:text-gray-600 text-textSecondary mb-4" />
          <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">No candidates found</span>
          <span className="text-gray-500 dark:text-gray-400 text-xs mt-1">Adjust filters to view candidates</span>
        </div>
      )}

      {!isLoading && totalCount > 0 && (
        <div className="flex flex-col gap-4">
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KPITile
              icon={<Users className="w-5 h-5" />}
              label="Total Candidates"
              value={summary.total_candidates}
              description="Total number of candidates matching your filters."
            />
            <KPITile
              icon={<SlidersHorizontal className="w-5 h-5" />}
              label="Average Score"
              value={`${avgScore.toFixed(1)}/100`}
              description="Mean score across the currently filtered candidates."
            />
            <KPITile
              icon={<Percent className="w-5 h-5" />}
              label="Selected Rate"
              value={`${selectedRate.toFixed(1)}%`}
              description="Selected candidates as a share of all candidates across every job (Date range and Min Score still apply)."
            />
          </div>

          {/* Chart panel */}
          <div className="h-[620px] dark:bg-gradient-to-r dark:from-[#232142]/10 dark:to-[#7E64A3]/10 bg-bgSecondary rounded-xl p-6 flex flex-col">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-[#404040] dark:text-[#AEB9E1]" />
                <h2 className="text-[#404040] dark:text-[#AEB9E1] text-lg font-medium tracking-wide">
                  Score Distribution
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    — {selectedJobPosition}
                  </span>
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {totalCount} candidates
                </span>
                <div className="tooltip" title="Blue: Above Average, Purple: Below Average" style={{ cursor: "pointer" }}>
                  <Info className="w-4 h-4 dark:text-gray-400 text-textSecondary" />
                </div>
              </div>
            </div>
            <div className="flex-1 min-h-0">{renderChart()}</div>
            <div className="flex items-center justify-between gap-2 flex-wrap mt-3 pt-3 border-t border-gray-200 dark:border-gray-700/50">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="p-1.5 border border-gray-300 rounded-full bg-bgPrimary text-textPrimary hover:border-gray-400 focus:outline-none dark:glass-effect dark:bg-[linear-gradient(233.57deg,rgba(34,52,97,0.25)_-5.01%,rgba(11,10,31,0.05)_100%)] dark:border-[rgba(229,229,229,0.5)] dark:text-[rgba(216,216,216,1)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="p-1.5 border border-gray-300 rounded-full bg-bgPrimary text-textPrimary hover:border-gray-400 focus:outline-none dark:glass-effect dark:bg-[linear-gradient(233.57deg,rgba(34,52,97,0.25)_-5.01%,rgba(11,10,31,0.05)_100%)] dark:border-[rgba(229,229,229,0.5)] dark:text-[rgba(216,216,216,1)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateAnalyticsDashboard;
